import * as React from "react";
import { forwardRef, useImperativeHandle, useRef, useState, useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { QuickAddProductModal } from "./QuickAddProductModal";
import { Input } from "@/components/ui/input";
import { DecimalInput } from "@/components/ui/decimal-input";
import { Button } from "@/components/ui/button";
import { Trash2, ChevronUp, ChevronDown, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { RaioXModal } from "@/components/radiox/RaioXModal";
import { useUnitConversion } from "@/hooks/useUnitConversion";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useFormWithFields } from "@/hooks/useForms";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { DateField } from "@/components/ui/date-field";
import { startOfDay, endOfDay, subDays } from "date-fns";
import ProductDetailsView from "@/pages/product/ProductDetailsView";

type SaleItem = {
  product_id: string;
  quantidade: number;
  preco_unitario: number;
  desconto: number;
  total: number;
  form_values?: Record<string, any>;
};

type Product = {
  id: string;
  nome: string;
  codigo_sku: string | null;
  descricao: string | null;
  preco_venda: number;
  status: string;
  form_id: string | null;
  unit_measurement_purchase_id: string | null;
  unit_measurement_sale_id: string | null;
  unit_measurement_conversions: Record<string, Record<string, number>> | null;
  quantity_decimal_places: number;
  foto_url: string | null;
  created_at: string | null;
  product_category_id: string | null;
};

type UnitMeasurement = {
  id: string;
  nome: string;
  slug: string;
};

interface SaleItemsCardsProps {
  items: SaleItem[];
  onItemsChange: (items: SaleItem[]) => void;
  onProductSelectOpenChange?: (open: boolean) => void;
  readOnly?: boolean;
}

export type SaleItemsCardsHandle = {
  openNewRowProductSelect: () => void;
  focusQuantity: (index?: number) => void;
  closeProductSelect: () => void;
};

export const SaleItemsCards = forwardRef<SaleItemsCardsHandle, SaleItemsCardsProps>(
function SaleItemsCardsComponent({ items, onItemsChange, onProductSelectOpenChange, readOnly = false }: SaleItemsCardsProps, ref) {
  const { calculatePrice } = useUnitConversion();
  const [productSelectOpenIndex, setProductSelectOpenIndex] = useState<number | null>(null);
  const [tempProductLabelByRow, setTempProductLabelByRow] = useState<Record<number, string>>({});
  const qtyRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [productProfile, setProductProfile] = useState<{ id: string; name: string } | null>(null);
  const [rxFrom, setRxFrom] = useState<Date | null>(null);
  const [rxTo, setRxTo] = useState<Date | null>(null);

  const { data: products } = useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .eq("status", "ativo")
        .order("nome");
      if (error) throw error;
      return (data || []) as unknown as Product[];
    },
  });

  const { data: unitMeasurements } = useQuery({
    queryKey: ["unit_measurements"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("unit_measurements" as any)
        .select("*")
        .order("nome", { ascending: true });
      if (error) throw error;
      return (data || []) as unknown as UnitMeasurement[];
    },
  });

  const calculateSubtotal = (quantidade: number, preco_unitario: number, desconto: number) => {
    return (preco_unitario - desconto) * quantidade;
  };

  const bumpQty = (index: number, delta: number) => {
    const current = items[index]?.quantidade ?? 0;
    const productId = items[index]?.product_id;
    const decimalPlaces = productId ? getQuantityDecimalPlaces(productId) : 0;
    const step = decimalPlaces === 0 ? 1 : Math.pow(10, -decimalPlaces);
    const next = Math.max(0, parseFloat((current + delta * step).toFixed(decimalPlaces)));
    if (readOnly) return;
    updateItem(index, 'quantidade', next);
  };

  const focusQuantity = (index: number = items.length - 1) => {
    const ref = qtyRefs.current[index];
    if (ref) {
      ref.focus();
      try { ref.select(); } catch {}
    }
  };

  useImperativeHandle(ref, () => ({
    openNewRowProductSelect: () => setProductSelectOpenIndex(items.length),
    focusQuantity,
    closeProductSelect: () => setProductSelectOpenIndex(null),
  }), [items.length]);

  React.useEffect(() => {
    onProductSelectOpenChange?.(productSelectOpenIndex !== null);
  }, [productSelectOpenIndex, onProductSelectOpenChange]);

  const handleProductChange = (
    productId: string,
    index: number,
    productHint?: { nome?: string; preco_venda?: number; unit_measurement_purchase_id?: string | null; unit_measurement_sale_id?: string | null }
  ) => {
    if (readOnly) return;
    const product = products?.find((p) => p.id === productId);
    const effectiveName = product?.nome ?? productHint?.nome ?? "";
    if (product || productHint) {
      let precoUnitario = Number(product?.preco_venda ?? productHint?.preco_venda ?? 0);
      
      if ((product?.unit_measurement_purchase_id || productHint?.unit_measurement_purchase_id) && (product?.unit_measurement_sale_id || productHint?.unit_measurement_sale_id)) {
        const purchaseUnit = unitMeasurements?.find(u => u.id === (product?.unit_measurement_purchase_id ?? productHint?.unit_measurement_purchase_id!));
        const saleUnit = unitMeasurements?.find(u => u.id === (product?.unit_measurement_sale_id ?? productHint?.unit_measurement_sale_id!));
        
        if (purchaseUnit && saleUnit && purchaseUnit.slug !== saleUnit.slug) {
          const convertedPrice = calculatePrice(
            Number(product?.preco_venda ?? productHint?.preco_venda ?? 0),
            1,
            purchaseUnit.slug,
            saleUnit.slug,
            product?.unit_measurement_conversions || undefined
          );
          
          if (convertedPrice !== null) {
            precoUnitario = convertedPrice;
          }
        }
      }
      
      const newItem: SaleItem = {
        product_id: productId,
        quantidade: 1,
        preco_unitario: precoUnitario,
        desconto: 0,
        total: precoUnitario,
      };
      
      const newItems = [...items];
      newItems[index] = newItem;
      onItemsChange(newItems);
      setProductSelectOpenIndex(null);
      setTempProductLabelByRow((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
      requestAnimationFrame(() => focusQuantity(index));
    }
  };

  const updateItem = (index: number, field: keyof SaleItem, value: number) => {
    if (readOnly) return;
    const newItems = [...items];
    newItems[index] = { 
      ...newItems[index], 
      [field]: value,
      total: calculateSubtotal(
        field === 'quantidade' ? value : newItems[index].quantidade,
        field === 'preco_unitario' ? value : newItems[index].preco_unitario,
        field === 'desconto' ? value : newItems[index].desconto
      )
    };
    onItemsChange(newItems);
  };

  const removeItem = (index: number) => {
    if (readOnly) return;
    onItemsChange(items.filter((_, i) => i !== index));
  };

  const getProductName = (productId: string) => {
    return products?.find((p) => p.id === productId)?.nome || "";
  };

  const getSaleUnitSlug = (productId: string) => {
    const product = products?.find((p) => p.id === productId);
    if (!product?.unit_measurement_sale_id) return null;
    const unit = unitMeasurements?.find(u => u.id === product.unit_measurement_sale_id);
    return unit?.slug || null;
  };

  const getPurchaseUnitSlug = (productId: string) => {
    const product = products?.find((p) => p.id === productId);
    if (!product?.unit_measurement_purchase_id) return null;
    const unit = unitMeasurements?.find(u => u.id === product.unit_measurement_purchase_id);
    return unit?.slug || null;
  };

  const getQuantityDecimalPlaces = (productId: string): number => {
    const product = products?.find((p) => p.id === productId);
    return product?.quantity_decimal_places ?? 0;
  };

  const getProductFormId = (productId: string): string | null => {
    const product = products?.find((p) => p.id === productId);
    return product?.form_id ?? null;
  };

  // Componente para campos de texto que usa estado local para evitar perda de foco
  const TextFieldComponent = React.memo(({ 
    field, 
    fieldValue, 
    updateFormValue, 
    readOnly 
  }: { 
    field: any; 
    fieldValue: string; 
    updateFormValue: (slug: string, value: any) => void; 
    readOnly: boolean;
  }) => {
    const [localValue, setLocalValue] = React.useState(fieldValue);
    const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
    const updateFormValueRef = React.useRef(updateFormValue);
    const isFocusedRef = React.useRef(false);
    const fieldValueRef = React.useRef(fieldValue);

    // Atualizar ref quando updateFormValue mudar
    React.useEffect(() => {
      updateFormValueRef.current = updateFormValue;
    }, [updateFormValue]);

    // Atualizar ref do fieldValue
    React.useEffect(() => {
      fieldValueRef.current = fieldValue;
    }, [fieldValue]);

    // Sincronizar com valor externo quando mudar (mas não quando estamos editando)
    React.useEffect(() => {
      // Só atualizar se não estiver focado e o valor realmente mudou
      if (!isFocusedRef.current && fieldValueRef.current !== fieldValue) {
        setLocalValue(fieldValue);
      }
    }, [fieldValue]);

    const handleFocus = React.useCallback(() => {
      isFocusedRef.current = true;
    }, []);

    const handleChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setLocalValue(newValue);
      
      // NÃO atualizar estado pai durante a digitação
      // Apenas atualizar no onBlur para evitar re-renderizações
    }, []);

    const handleBlur = React.useCallback(() => {
      isFocusedRef.current = false;
      // Atualizar estado pai imediatamente ao sair do campo
      updateFormValueRef.current(field.slug, localValue);
    }, [field.slug, localValue]);

    React.useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    return (
      <div className="space-y-0 flex-1 min-w-[200px]">
        <Label htmlFor={`form-${field.id}`} className="text-xs text-muted-foreground uppercase tracking-wide mb-0.5 block">
          {field.nome}
          {field.obrigatorio && <span className="text-destructive"> *</span>}
        </Label>
        <Input
          id={`form-${field.id}`}
          value={localValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={field.placeholder || ""}
          disabled={readOnly}
        />
        {field.descricao && (
          <p className="text-xs text-muted-foreground">{field.descricao}</p>
        )}
      </div>
    );
  }, (prevProps, nextProps) => {
    // Comparação customizada: só re-renderiza se field ou readOnly mudarem
    // Ignora mudanças em fieldValue e updateFormValue durante a digitação
    return (
      prevProps.field.id === nextProps.field.id &&
      prevProps.readOnly === nextProps.readOnly &&
      prevProps.field.nome === nextProps.field.nome &&
      prevProps.field.obrigatorio === nextProps.field.obrigatorio &&
      prevProps.field.placeholder === nextProps.field.placeholder &&
      prevProps.field.descricao === nextProps.field.descricao
    );
  });

  const NumberFieldComponent = React.memo(({ 
    field, 
    fieldValue, 
    updateFormValue, 
    readOnly 
  }: { 
    field: any; 
    fieldValue: string; 
    updateFormValue: (slug: string, value: any) => void; 
    readOnly: boolean;
  }) => {
    const [localValue, setLocalValue] = React.useState(fieldValue);
    const updateFormValueRef = React.useRef(updateFormValue);
    const isFocusedRef = React.useRef(false);
    const fieldValueRef = React.useRef(fieldValue);

    React.useEffect(() => { updateFormValueRef.current = updateFormValue; }, [updateFormValue]);
    React.useEffect(() => { fieldValueRef.current = fieldValue; }, [fieldValue]);
    React.useEffect(() => { if (!isFocusedRef.current && fieldValueRef.current !== fieldValue) { setLocalValue(fieldValue); } }, [fieldValue]);

    const handleFocus = React.useCallback(() => { isFocusedRef.current = true; }, []);
    const handleBlur = React.useCallback(() => {
      isFocusedRef.current = false;
      updateFormValueRef.current(field.slug, localValue);
    }, [field.slug, localValue]);

    return (
      <div className="space-y-0 flex-1 min-w-[200px]">
        <Label htmlFor={`form-${field.id}`} className="text-xs text-muted-foreground uppercase tracking-wide mb-0.5 block">
          {field.nome}
          {field.obrigatorio && <span className="text-destructive"> *</span>}
        </Label>
        <Input
          id={`form-${field.id}`}
          type="number"
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={field.placeholder || ""}
          disabled={readOnly}
        />
        {field.descricao && (
          <p className="text-xs text-muted-foreground">{field.descricao}</p>
        )}
      </div>
    );
  });

  const TextAreaFieldComponent = React.memo(({ 
    field, 
    fieldValue, 
    updateFormValue, 
    readOnly 
  }: { 
    field: any; 
    fieldValue: string; 
    updateFormValue: (slug: string, value: any) => void; 
    readOnly: boolean;
  }) => {
    const [localValue, setLocalValue] = React.useState(fieldValue);
    const updateFormValueRef = React.useRef(updateFormValue);
    const isFocusedRef = React.useRef(false);
    const fieldValueRef = React.useRef(fieldValue);

    React.useEffect(() => { updateFormValueRef.current = updateFormValue; }, [updateFormValue]);
    React.useEffect(() => { fieldValueRef.current = fieldValue; }, [fieldValue]);
    React.useEffect(() => { if (!isFocusedRef.current && fieldValueRef.current !== fieldValue) { setLocalValue(fieldValue); } }, [fieldValue]);

    const handleFocus = React.useCallback(() => { isFocusedRef.current = true; }, []);
    const handleBlur = React.useCallback(() => {
      isFocusedRef.current = false;
      updateFormValueRef.current(field.slug, localValue);
    }, [field.slug, localValue]);

    return (
      <div className="space-y-0 w-full">
        <Label htmlFor={`form-${field.id}`} className="text-xs text-muted-foreground uppercase tracking-wide mb-0.5 block">
          {field.nome}
          {field.obrigatorio && <span className="text-destructive"> *</span>}
        </Label>
        <Textarea
          id={`form-${field.id}`}
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={field.placeholder || ""}
          disabled={readOnly}
          rows={3}
        />
        {field.descricao && (
          <p className="text-xs text-muted-foreground">{field.descricao}</p>
        )}
      </div>
    );
  });

  // Componente para renderizar campos do formulário
  const FormFieldsSection = React.memo(({ item, index }: { item: SaleItem; index: number }) => {
    const formId = item.product_id ? getProductFormId(item.product_id) : null;
    
    // Usar ref para items e onItemsChange para evitar recriação da função
    // IMPORTANTE: Hooks devem ser chamados antes de qualquer return condicional
    const itemsRef = useRef(items);
    const onItemsChangeRef = useRef(onItemsChange);
    
    React.useEffect(() => {
      itemsRef.current = items;
      onItemsChangeRef.current = onItemsChange;
    }, [items, onItemsChange]);
    
    // Verificar se há campos que precisam de dependências antes de buscar
    // Para campos de texto, não precisamos buscar dependências
    const { data: formData, isLoading } = useFormWithFields(formId || "", {
      enabled: !!formId && !!item.product_id,
      refetchOnMount: false,
      staleTime: 5 * 60 * 1000,
    });

    // Apenas buscar relacionamentos de opções se houver campos que têm opções
    const hasFieldsWithOptions = useMemo(() => {
      const normalize = (t: string) => {
        const s = (t || "").toLowerCase();
        if (["seleção única", "selecao unica", "select"].includes(s)) return "select";
        if (["múltipla seleção", "multipla selecao", "multiselect"].includes(s)) return "multiselect";
        if (["radio"].includes(s)) return "radio";
        if (["checkbox"].includes(s)) return "checkbox";
        return s;
      };
      return formData?.fields?.some((f: any) => ["select", "multiselect", "radio", "checkbox"].includes(normalize(f.tipo))) ?? false;
    }, [formData?.fields]);

    // Calcular IDs de opções apenas quando necessário
    const allOptionIds = useMemo(() => {
      if (!formData?.fields || !hasFieldsWithOptions) return [];
      const normalize = (t: string) => {
        const s = (t || "").toLowerCase();
        if (["seleção única", "selecao unica", "select"].includes(s)) return "select";
        if (["múltipla seleção", "multipla selecao", "multiselect"].includes(s)) return "multiselect";
        if (["radio"].includes(s)) return "radio";
        if (["checkbox"].includes(s)) return "checkbox";
        return s;
      };
      return formData.fields
        .filter((f: any) => ["select", "multiselect", "radio", "checkbox"].includes(normalize(f.tipo)))
        .flatMap((f: any) => {
          const flatten = (opts: any[]): string[] => {
            const ids: string[] = [];
            for (const opt of opts || []) {
              ids.push(opt.id);
              if (opt.sub_opcoes && opt.sub_opcoes.length > 0) {
                ids.push(...flatten(opt.sub_opcoes));
              }
            }
            return ids;
          };
          return flatten(f.options || []);
        });
    }, [formData?.fields, hasFieldsWithOptions]);

    const { data: optionRelations = [] } = useQuery({
      queryKey: ["form-option-relations", formId, allOptionIds.sort().join(",")],
      enabled:
        !!formId &&
        !!formData?.fields &&
        hasFieldsWithOptions &&
        !isLoading &&
        allOptionIds.length > 0,
      staleTime: 5 * 60 * 1000, // 5 minutos - dados não ficam "stale" por 5 minutos
      gcTime: 10 * 60 * 1000, // 10 minutos - cache mantido por 10 minutos
      queryFn: async () => {
        if (allOptionIds.length === 0) return [];

        const { data, error } = await supabase
          .from("form_field_option_relations")
          .select("*")
          .in("option_id", allOptionIds);

        if (error && error.code !== "PGRST116") {
          console.error("Erro ao buscar relacionamentos:", error);
          return [];
        }

        return data || [];
      },
    });

    // Usar ref para item para evitar recriação de getFieldValue
    // IMPORTANTE: Hooks devem ser chamados antes de qualquer return condicional
    const itemRef = useRef(item);
    React.useEffect(() => {
      itemRef.current = item;
    }, [item]);

    const getFieldValue = useCallback((fieldSlug: string) => {
      return itemRef.current.form_values?.[fieldSlug] ?? "";
    }, []);

    const getBaseUnitPrice = (productId?: string): number => {
      if (!productId) return 0;
      const product = (products || []).find((p) => p.id === productId);
      let base = Number(product?.preco_venda || 0);
      const purchaseUnit = product?.unit_measurement_purchase_id ? unitMeasurements?.find(u => u.id === product.unit_measurement_purchase_id) : null;
      const saleUnit = product?.unit_measurement_sale_id ? unitMeasurements?.find(u => u.id === product.unit_measurement_sale_id) : null;
      if (purchaseUnit && saleUnit && purchaseUnit.slug !== saleUnit.slug) {
        const converted = calculatePrice(base, 1, purchaseUnit.slug, saleUnit.slug, product?.unit_measurement_conversions || undefined);
        if (converted !== null) base = converted;
      }
      return base;
    };

    const computeUnitPriceFromForm = (formValues: Record<string, any>): number => {
      const base = getBaseUnitPrice(item.product_id);
      const modifierFields = (formData?.fields || []).filter((f: any) => !!f.modifica_valor_produto);
      if (modifierFields.length === 0) return base;
      let price = 0;
      for (const f of modifierFields) {
        const v = formValues?.[f.slug];
        if (v === undefined || v === null || v === "") continue;
        const flatten = (opts: any[]): any[] => {
          const res: any[] = [];
          for (const o of opts || []) {
            res.push(o);
            if (o.sub_opcoes && o.sub_opcoes.length > 0) res.push(...flatten(o.sub_opcoes));
          }
          return res;
        };
        const all = flatten(f.options || []);
        const values = Array.isArray(v) ? v : [v];
        const selected = values
          .map((val) => all.find((o: any) => o.valor === val))
          .filter(Boolean);
        if (selected.length > 0) {
          const fieldSum = selected.reduce((acc: number, o: any) => acc + Number(o.valor_produto || 0), 0);
          price += fieldSum;
        }
      }
      return price > 0 ? price : base;
    };

    const updateFormValue = useCallback((fieldSlug: string, value: any) => {
      if (readOnly) return;
      const newItems = [...itemsRef.current];
      const currentFormValues = newItems[index].form_values || {};
      
      const field = formData?.fields?.find((f: any) => f.slug === fieldSlug);
      
      // Apenas processar dependências para campos que podem ter dependências
      // (select, multiselect, radio) - campos de texto não têm dependências
      if (field && ["select", "multiselect", "radio"].includes(field.tipo)) {
        const dependentFields = formData.dependencies
          .filter((d: any) => d.depends_on_field_id === field.id)
          .map((d: any) => formData.fields.find((f: any) => f.id === d.field_id))
          .filter(Boolean);

        const updatedFormValues = { ...currentFormValues, [fieldSlug]: value };
        
        dependentFields.forEach((depField: any) => {
          if (depField) {
            delete updatedFormValues[depField.slug];
          }
        });

        const nextUnit = computeUnitPriceFromForm(updatedFormValues);
        newItems[index] = {
          ...newItems[index],
          form_values: updatedFormValues,
          preco_unitario: nextUnit,
          total: (nextUnit - newItems[index].desconto) * newItems[index].quantidade,
        };
      } else {
        const tmpValues = { ...currentFormValues, [fieldSlug]: value };
        const nextUnit = computeUnitPriceFromForm(tmpValues);
        newItems[index] = {
          ...newItems[index],
          form_values: tmpValues,
          preco_unitario: nextUnit,
          total: (nextUnit - newItems[index].desconto) * newItems[index].quantidade,
        };
      }
      
      onItemsChangeRef.current(newItems);
    }, [readOnly, index, formData]);

    // Verificar condições após todos os hooks serem chamados
    if (!formId || !formData?.fields || formData.fields.length === 0) {
      return null;
    }

    const getFilteredOptions = (field: any) => {
      if (!field.options || field.options.length === 0) {
        return [];
      }

      const dependency = formData.dependencies.find((d: any) => d.field_id === field.id);
      
      if (!dependency) {
        const flatten = (opts: any[]): any[] => {
          const result: any[] = [];
          for (const opt of opts || []) {
            const { sub_opcoes, ...optWithoutSubs } = opt;
            result.push(optWithoutSubs);
            if (sub_opcoes && sub_opcoes.length > 0) {
              result.push(...flatten(sub_opcoes));
            }
          }
          return result;
        };
        return flatten(field.options);
      }

      const dependentField = formData.fields.find((f: any) => f.id === dependency.depends_on_field_id);
      if (!dependentField) {
        return [];
      }

      const dependentFieldValue = getFieldValue(dependentField.slug);
      if (dependentFieldValue === undefined || dependentFieldValue === null || dependentFieldValue === "") {
        return [];
      }

      const flatten = (opts: any[]): any[] => {
        const result: any[] = [];
        for (const opt of opts || []) {
          const { sub_opcoes, ...optWithoutSubs } = opt;
          result.push(optWithoutSubs);
          if (sub_opcoes && sub_opcoes.length > 0) {
            result.push(...flatten(sub_opcoes));
          }
        }
        return result;
      };

      const dependentFieldOptionsFlat = flatten(dependentField.options || []);
      const valuesArray = Array.isArray(dependentFieldValue) ? dependentFieldValue : [dependentFieldValue];
      const selectedParentOptions = valuesArray
        .map((val: any) => dependentFieldOptionsFlat.find((opt: any) => opt.valor === val))
        .filter(Boolean);

      if (selectedParentOptions.length === 0) {
        return [];
      }

      const relatedOptionIdsSet = new Set<string>();
      optionRelations
        .filter((rel: any) => selectedParentOptions.some((sel: any) => rel.related_option_id === sel.id))
        .forEach((rel: any) => relatedOptionIdsSet.add(rel.option_id));

      if (relatedOptionIdsSet.size > 0) {
        const allOptionsFlat = flatten(field.options);
        return allOptionsFlat.filter((opt: any) => relatedOptionIdsSet.has(opt.id));
      }

      const findOptionInTree = (opts: any[], valor: string): any => {
        for (const opt of opts) {
          if (opt.valor === valor) {
            return opt;
          }
          if (opt.sub_opcoes && opt.sub_opcoes.length > 0) {
            const found = findOptionInTree(opt.sub_opcoes, valor);
            if (found) return found;
          }
        }
        return null;
      };

      const selectedOptionsHier = valuesArray
        .map((val: any) => findOptionInTree(dependentField.options || [], val))
        .filter((opt: any) => opt && opt.sub_opcoes && opt.sub_opcoes.length > 0)
        .flatMap((opt: any) => opt.sub_opcoes);
      if (selectedOptionsHier.length > 0) {
        return selectedOptionsHier;
      }

      return [];
    };

    if (isLoading) {
      return (
        <div className="mt-4 pt-4 border-t">
          <div className="text-sm text-muted-foreground">Carregando formulário...</div>
        </div>
      );
    }

    return (
      <div className="mt-0 pt-0 border-t">
        <div className="flex flex-wrap gap-4 pt-2">
          {formData.fields.map((field: any) => {
            const fieldValue = getFieldValue(field.slug);

            const normalizeType = (t: string) => {
              const s = (t || "").toLowerCase();
              if (["área de texto", "area de texto", "textarea"].includes(s)) return "textarea";
              if (["texto", "text"].includes(s)) return "text";
              if (["número", "numero", "number"].includes(s)) return "number";
              if (["data e hora", "datetime", "datahora"].includes(s)) return "datetime";
              if (["data", "date"].includes(s)) return "date";
              if (["e-mail", "email"].includes(s)) return "email";
              if (["telefone", "tel", "phone"].includes(s)) return "tel";
              if (["seleção única", "selecao unica", "select"].includes(s)) return "select";
              if (["múltipla seleção", "multipla selecao", "multiplas seleções", "multiplas selecao", "multiselect"].includes(s)) return "multiselect";
              if (["checkbox"].includes(s)) return "checkbox";
              if (["radio"].includes(s)) return "radio";
              if (["arquivos", "arquivo", "files", "file"].includes(s)) return "file";
              if (["imagens", "imagem", "images", "image"].includes(s)) return "image";
              return s;
            };

            switch (normalizeType(field.tipo)) {
              case "text":
              case "email":
              case "tel":
                return (
                  <TextFieldComponent
                    key={field.id}
                    field={field}
                    fieldValue={fieldValue}
                    updateFormValue={updateFormValue}
                    readOnly={readOnly}
                  />
                );
              
              case "textarea":
                return (
                  <TextAreaFieldComponent
                    key={field.id}
                    field={field}
                    fieldValue={fieldValue}
                    updateFormValue={updateFormValue}
                    readOnly={readOnly}
                  />
                );
              
              case "number":
                return (
                  <NumberFieldComponent
                    key={field.id}
                    field={field}
                    fieldValue={fieldValue}
                    updateFormValue={updateFormValue}
                    readOnly={readOnly}
                  />
                );

              case "date":
                return (
                  <div key={field.id} className="space-y-0 flex-1 min-w-[200px]">
                    <Label htmlFor={`form-${field.id}`} className="text-xs text-muted-foreground uppercase tracking-wide mb-0.5 block">
                      {field.nome}
                      {field.obrigatorio && <span className="text-destructive"> *</span>}
                    </Label>
                    <DateField
                      id={`form-${field.id}`}
                      value={fieldValue || ""}
                      onChange={(e) => updateFormValue(field.slug, (e.target as HTMLInputElement).value)}
                      disabled={readOnly}
                    />
                    {field.descricao && (
                      <p className="text-xs text-muted-foreground">{field.descricao}</p>
                    )}
                  </div>
                );

              case "datetime":
                return (
                  <div key={field.id} className="space-y-0 flex-1 min-w-[200px]">
                    <Label htmlFor={`form-${field.id}`} className="text-xs text-muted-foreground uppercase tracking-wide mb-0.5 block">
                      {field.nome}
                      {field.obrigatorio && <span className="text-destructive"> *</span>}
                    </Label>
                    <Input
                      id={`form-${field.id}`}
                      type="datetime-local"
                      value={fieldValue || ""}
                      onChange={(e) => updateFormValue(field.slug, e.target.value)}
                      disabled={readOnly}
                    />
                    {field.descricao && (
                      <p className="text-xs text-muted-foreground">{field.descricao}</p>
                    )}
                  </div>
                );
              
              case "select":
                const filteredOptions = getFilteredOptions(field);
                const hasDependency = formData.dependencies.some((d: any) => d.field_id === field.id);
                return (
                  <div key={field.id} className="space-y-0 flex-1 min-w-[200px]">
                    <Label htmlFor={`form-${field.id}`} className="text-xs text-muted-foreground uppercase tracking-wide mb-0.5 block">
                      {field.nome}
                      {field.obrigatorio && <span className="text-destructive"> *</span>}
                    </Label>
                    <Select
                      value={fieldValue || "__empty__"}
                      onValueChange={(value) => updateFormValue(field.slug, value === "__empty__" ? "" : value)}
                      disabled={readOnly || (hasDependency && filteredOptions.length === 0)}
                    >
                      <SelectTrigger id={`form-${field.id}`}>
                        <SelectValue placeholder={hasDependency && filteredOptions.length === 0 ? "Selecione o campo anterior primeiro" : `Selecione ${field.nome}`} />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredOptions.length > 0 ? (
                          <>
                            <SelectItem value="__empty__">
                              Selecione {field.nome}
                            </SelectItem>
                            {filteredOptions.map((option: any) => (
                              <SelectItem key={option.id} value={option.valor}>
                                {field.modifica_valor_produto && option.valor_produto != null ? `${new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(option.valor_produto)} - ${option.label}` : option.label}
                              </SelectItem>
                            ))}
                          </>
                        ) : (
                          <SelectItem value="__no_options__" disabled>
                            {hasDependency ? "Selecione o campo anterior primeiro" : "Nenhuma opção disponível"}
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    {field.descricao && (
                      <p className="text-xs text-muted-foreground">{field.descricao}</p>
                    )}
                  </div>
                );

              case "multiselect":
                const filteredMultiOptions = getFilteredOptions(field);
                const hasMultiDependency = formData.dependencies.some((d: any) => d.field_id === field.id);
                const multiValues = Array.isArray(fieldValue) ? fieldValue : fieldValue ? [fieldValue] : [];
                return (
                  <div key={field.id} className="space-y-0 flex-1 min-w-[200px]">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wide mb-0.5 block">
                      {field.nome}
                      {field.obrigatorio && <span className="text-destructive"> *</span>}
                    </Label>
                    <div className="space-y-0.5">
                      {filteredMultiOptions.length > 0 ? (
                        filteredMultiOptions.map((option: any) => (
                          <div key={option.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`multiselect-${option.id}`}
                              checked={multiValues.includes(option.valor)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  updateFormValue(field.slug, [...multiValues, option.valor]);
                                } else {
                                  updateFormValue(field.slug, multiValues.filter((v: string) => v !== option.valor));
                                }
                              }}
                              disabled={readOnly || (hasMultiDependency && filteredMultiOptions.length === 0)}
                            />
                            <Label htmlFor={`multiselect-${option.id}`} className="font-normal cursor-pointer">
                              {field.modifica_valor_produto && option.valor_produto != null ? `${new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(option.valor_produto)} - ${option.label}` : option.label}
                            </Label>
                          </div>
                        ))
                      ) : (
                        <div className="text-sm text-muted-foreground">
                          {hasMultiDependency ? "Selecione o campo anterior primeiro" : "Nenhuma opção disponível"}
                        </div>
                      )}
                    </div>
                    {field.descricao && (
                      <p className="text-xs text-muted-foreground">{field.descricao}</p>
                    )}
                  </div>
                );
              
              case "radio":
                const filteredRadioOptions = getFilteredOptions(field);
                const hasRadioDependency = formData.dependencies.some((d: any) => d.field_id === field.id);
                return (
                  <div key={field.id} className="space-y-0 flex-1 min-w-[200px]">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wide mb-0.5 block">
                      {field.nome}
                      {field.obrigatorio && <span className="text-destructive"> *</span>}
                    </Label>
                    <RadioGroup
                      value={fieldValue || ""}
                      onValueChange={(value) => updateFormValue(field.slug, value)}
                      disabled={readOnly || (hasRadioDependency && filteredRadioOptions.length === 0)}
                    >
                      {filteredRadioOptions.length > 0 ? (
                        filteredRadioOptions.map((option: any) => (
                          <div key={option.id} className="flex items-center space-x-2">
                            <RadioGroupItem value={option.valor} id={`radio-${option.id}`} />
                            <Label htmlFor={`radio-${option.id}`} className="font-normal cursor-pointer">
                              {field.modifica_valor_produto && option.valor_produto != null ? `${new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(option.valor_produto)} - ${option.label}` : option.label}
                            </Label>
                          </div>
                        ))
                      ) : (
                        <div className="text-sm text-muted-foreground">
                          {hasRadioDependency ? "Selecione o campo anterior primeiro" : "Nenhuma opção disponível"}
                        </div>
                      )}
                    </RadioGroup>
                    {field.descricao && (
                      <p className="text-xs text-muted-foreground">{field.descricao}</p>
                    )}
                  </div>
                );
              
              case "checkbox":
                const filteredCheckboxOptions = getFilteredOptions(field);
                const hasCheckboxDependency = formData.dependencies.some((d: any) => d.field_id === field.id);
                const checkedValues = Array.isArray(fieldValue) ? fieldValue : fieldValue ? [fieldValue] : [];
                return (
                  <div key={field.id} className="space-y-0 flex-1 min-w-[200px]">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wide mb-0.5 block">
                      {field.nome}
                      {field.obrigatorio && <span className="text-destructive"> *</span>}
                    </Label>
                    <div className="space-y-0.5">
                      {filteredCheckboxOptions.length > 0 ? (
                        filteredCheckboxOptions.map((option: any) => (
                          <div key={option.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`checkbox-${option.id}`}
                              checked={checkedValues.includes(option.valor)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  updateFormValue(field.slug, [...checkedValues, option.valor]);
                                } else {
                                  updateFormValue(field.slug, checkedValues.filter((v: string) => v !== option.valor));
                                }
                              }}
                              disabled={readOnly}
                            />
                            <Label htmlFor={`checkbox-${option.id}`} className="font-normal cursor-pointer">
                              {field.modifica_valor_produto && option.valor_produto != null ? `${new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(option.valor_produto)} - ${option.label}` : option.label}
                            </Label>
                          </div>
                        ))
                      ) : (
                        <div className="text-sm text-muted-foreground">
                          {hasCheckboxDependency ? "Selecione o campo anterior primeiro" : "Nenhuma opção disponível"}
                        </div>
                      )}
                    </div>
                    {field.descricao && (
                      <p className="text-xs text-muted-foreground">{field.descricao}</p>
                    )}
                  </div>
                );

              case "file":
              case "image":
                const isImage = normalizeType(field.tipo) === "image";
                return (
                  <div key={field.id} className="space-y-0 flex-1 min-w-[200px]">
                    <Label htmlFor={`form-${field.id}`} className="text-xs text-muted-foreground uppercase tracking-wide mb-0.5 block">
                      {field.nome}
                      {field.obrigatorio && <span className="text-destructive"> *</span>}
                    </Label>
                    <Input
                      id={`form-${field.id}`}
                      type="file"
                      accept={isImage ? "image/*" : "image/*,video/*,audio/*"}
                      disabled={readOnly}
                      onChange={async (e) => {
                        const file = e.target.files?.[0] || null;
                        if (!file) return;
                        const formDataBody = new FormData();
                        formDataBody.append("file", file);
                        try {
                          const { data, error } = await (supabase as any).functions.invoke("ai-upload-asset", { body: formDataBody });
                          if (!error && data && data.path) {
                            const value = {
                              bucket: data.bucket,
                              path: data.path,
                              signed_url: data.signed_url,
                              mime_type: data.mime_type,
                              media_type: data.media_type,
                              original_name: file.name,
                              uploaded_at: data.uploaded_at,
                              expires_at: data.expires_at,
                            };
                            updateFormValue(field.slug, value);
                          }
                        } catch {}
                      }}
                    />
                    {fieldValue && typeof fieldValue === "object" && (fieldValue as any).signed_url && (
                      <div className="pt-1">
                        {isImage || (((fieldValue as any).mime_type || "").startsWith("image/")) ? (
                          <img src={(fieldValue as any).signed_url} alt={(fieldValue as any).original_name || "imagem"} className="h-16 w-auto rounded border" />
                        ) : (
                          <a href={(fieldValue as any).signed_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 underline">
                            {(fieldValue as any).original_name || (fieldValue as any).path}
                          </a>
                        )}
                      </div>
                    )}
                    {field.descricao && (
                      <p className="text-xs text-muted-foreground">{field.descricao}</p>
                    )}
                  </div>
                );

              default:
                return null;
            }
          })}
                      </div>
                    </div>
    );
  }, (prevProps, nextProps) => {
    // Comparação customizada para evitar re-renderizações desnecessárias
    // Só re-renderiza se o product_id mudar (não re-renderiza quando form_values mudam durante digitação)
    // Também verifica se o item em si mudou (exceto form_values)
    const prevItemWithoutFormValues = { ...prevProps.item };
    delete prevItemWithoutFormValues.form_values;
    const nextItemWithoutFormValues = { ...nextProps.item };
    delete nextItemWithoutFormValues.form_values;
    
    return (
      prevProps.item.product_id === nextProps.item.product_id &&
      prevProps.index === nextProps.index &&
      JSON.stringify(prevItemWithoutFormValues) === JSON.stringify(nextItemWithoutFormValues)
    );
  });

  const displayItems = [...items];
  if (!readOnly) {
    if (displayItems.length === 0 || displayItems[displayItems.length - 1].product_id) {
      displayItems.push({
        product_id: "",
        quantidade: 1,
        preco_unitario: 0,
        desconto: 0,
        total: 0,
      });
    }
  }

  return (
    <div className="space-y-3">
      <h3 className="font-semibold">Itens da Venda</h3>
      <div className="space-y-3">
        {displayItems.map((item, index) => {
          const isNewRow = index >= items.length;

          return (
            <Card 
              key={`card-${index}`}
              className={`${!isNewRow ? "bg-muted/50" : "bg-background"} ${isNewRow ? "cursor-pointer border-dashed" : ""} relative border-gray-300 border`}
              style={{ borderWidth: 3 }}
              onClick={() => {
                if (isNewRow) setProductSelectOpenIndex(index);
              }}
            >
              <CardContent className="px-3 pb-2 pt-1">
                {/* Lixeira no canto superior direito */}
                {!readOnly && !isNewRow && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      removeItem(index); 
                    }}
                    className="absolute top-2 right-2 h-8 w-8 icon-chip-red z-10"
                    data-testid="remove-item-button"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
                <div className="space-y-1.5">
                  {/* Cabeçalho do item (labels) */}
                    {/* Labels */}
                    {!isNewRow && (
                      <div className="flex items-center gap-3 mb-0">
                        <div className="w-[35%] min-w-0">
                          <Label className="text-xs text-muted-foreground uppercase tracking-wide">Produto</Label>
                        </div>
                        {item.product_id && (
                          <>
                            <div className="w-[20%] text-right">
                              <Label className="text-xs text-muted-foreground uppercase tracking-wide">Preço</Label>
                            </div>
                            <div className="w-[18%]">
                              <Label className="text-xs text-muted-foreground uppercase tracking-wide">Qtd</Label>
                            </div>
                            <div className="w-[17%] text-right">
                              <Label className="text-xs text-muted-foreground uppercase tracking-wide">Subtotal</Label>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                    {/* Campos */}
                    <div className={`flex items-center gap-3 ${isNewRow ? '' : '-mt-0.5'} rounded-md p-2`}>
                      {/* Produto */}
                      {isNewRow ? (
                        <div className="flex items-center gap-3 w-full">
                          <Label className="text-sm text-blue-600 font-semibold whitespace-nowrap">Adicionar um produto à venda:</Label>
                          <div className="flex-1 min-w-0">
                            <SearchableSelect
                              value={item.product_id || ""}
                              open={productSelectOpenIndex === index}
                              onOpenChange={(open) => setProductSelectOpenIndex(open ? index : null)}
                              onChange={(value) => handleProductChange(value, index)}
                              options={(products ?? []).map((p) => ({ value: p.id, label: p.nome }))}
                              placeholder="Selecione o produto"
                              withBackdrop={false}
                              valueLabel={tempProductLabelByRow[index]}
                              renderAddModal={index === displayItems.length - 1 ? ({ open, onOpenChange, onCreated }) => (
                                <QuickAddProductModal
                                  open={open}
                                  onOpenChange={onOpenChange}
                                  onProductCreated={({ id, nome, preco_venda, unit_measurement_purchase_id, unit_measurement_sale_id }) => {
                                    setTempProductLabelByRow((prev) => ({ ...prev, [index]: nome }));
                                    handleProductChange(id, index, {
                                      nome,
                                      preco_venda,
                                      unit_measurement_purchase_id,
                                      unit_measurement_sale_id,
                                    });
                                    onCreated(id);
                                  }}
                                />
                              ) : undefined}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="w-[35%] flex items-center gap-2 min-w-0">
                          {readOnly ? (
                            <React.Fragment>
                              <span className="text-sm font-medium line-clamp-1 flex-1 min-w-0">
                                {item.product_id ? (getProductName(item.product_id) || "-") : "-"}
                              </span>
                              {item.product_id && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setProductProfile({ id: item.product_id, name: getProductName(item.product_id) || "produto" });
                                        }}
                                        className="shrink-0 hover:text-blue-600 transition-colors cursor-pointer"
                                      >
                                        <FileText className="h-4 w-4 text-muted-foreground hover:text-blue-600 transition-colors" />
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent className="whitespace-pre-line z-[9999]" side="top" sideOffset={5}>
                                      Ver ficha completa e relatórios{'\n'}do produto <strong>{getProductName(item.product_id) || 'produto'}</strong>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </React.Fragment>
                          ) : (
                            <React.Fragment>
                              <div className="flex-1 min-w-0">
                                <SearchableSelect
                                  open={productSelectOpenIndex === index}
                                  value={item.product_id}
                                  onChange={(value) => handleProductChange(value, index)}
                                  options={(products ?? []).map((p) => ({ value: p.id, label: p.nome }))}
                                  placeholder="Selecione o produto"
                                  withBackdrop={false}
                                  onOpenChange={(open) => {
                                    setProductSelectOpenIndex((prev) => (open ? index : (prev === index ? null : prev)));
                                  }}
                                  valueLabel={tempProductLabelByRow[index]}
                                  renderAddModal={index === displayItems.length - 1 ? ({ open, onOpenChange, onCreated }) => (
                                    <QuickAddProductModal
                                      open={open}
                                      onOpenChange={onOpenChange}
                                      onProductCreated={({ id, nome, preco_venda, unit_measurement_purchase_id, unit_measurement_sale_id }) => {
                                        setTempProductLabelByRow((prev) => ({ ...prev, [index]: nome }));
                                        handleProductChange(id, index, {
                                          nome,
                                          preco_venda,
                                          unit_measurement_purchase_id,
                                          unit_measurement_sale_id,
                                        });
                                        onCreated(id);
                                      }}
                                    />
                                  ) : undefined}
                                />
                              </div>
                              {item.product_id && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setProductProfile({ id: item.product_id, name: getProductName(item.product_id) || "produto" });
                                        }}
                                        className="shrink-0 hover:text-blue-600 transition-colors cursor-pointer"
                                      >
                                        <FileText className="h-4 w-4 text-muted-foreground hover:text-blue-600 transition-colors" />
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent className="whitespace-pre-line z-[9999]" side="top" sideOffset={5}>
                                      Ver ficha completa e relatórios{'\n'}do produto <strong>{getProductName(item.product_id) || 'produto'}</strong>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </React.Fragment>
                          )}
                        </div>
                      )}
                      
                      {!isNewRow && item.product_id && (
                        <div className="w-[20%] text-right">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="text-sm font-medium cursor-default">
                                  {new Intl.NumberFormat("pt-BR", {
                                    style: "currency",
                                    currency: "BRL",
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  }).format(item.preco_unitario || 0)}
                                  {getSaleUnitSlug(item.product_id) && (
                                    <span className="text-muted-foreground"> /{getSaleUnitSlug(item.product_id)}</span>
                                  )}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="top" align="end">
                                {(() => {
                                  const product = products?.find(p => p.id === item.product_id);
                                  const purchaseSlug = getPurchaseUnitSlug(item.product_id);
                                  const base = product?.preco_venda ?? 0;
                                  const formatted = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(base);
                                  return <span>{formatted}{purchaseSlug ? ` /${purchaseSlug}` : ""}</span>;
                                })()}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      )}

                      
                      {!isNewRow && item.product_id && (
                        <div className="w-[18%]">
                          <div className="flex items-center gap-1">
                          <div className="relative flex-1">
                            <DecimalInput
                              value={item.quantidade}
                              decimalPlaces={item.product_id ? getQuantityDecimalPlaces(item.product_id) : 0}
                              onFocus={(e) => {
                                try { e.currentTarget.select(); } catch {}
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                try { e.currentTarget.select(); } catch {}
                              }}
                              onMouseDown={(e) => {
                                const el = e.currentTarget;
                                if (document.activeElement !== el) {
                                  e.preventDefault();
                                  el.focus();
                                  try { el.select(); } catch {}
                                }
                              }}
                              onValueChange={(value) => {
                                if (readOnly) return;
                                if (isNewRow || !item.product_id) return;
                                updateItem(index, 'quantidade', value);
                              }}
                              onBlur={(e) => {
                                if (readOnly) return;
                                if (isNewRow || !item.product_id) return;
                                const currentValue = item.quantidade ?? 0;
                                updateItem(index, 'quantidade', currentValue);
                              }}
                              disabled={readOnly || isNewRow || !item.product_id}
                              className={`pr-7 no-spinner text-sm ${(readOnly || isNewRow) ? "pointer-events-none" : ""}`}
                              ref={(el) => { qtyRefs.current[index] = el; }}
                            />
                            {getSaleUnitSlug(item.product_id) && (
                              <span className="pointer-events-none absolute right-1 top-1/2 -translate-y-[55%] text-sm leading-none text-muted-foreground font-medium">
                                {getSaleUnitSlug(item.product_id)}
                              </span>
                            )}
                          </div>
                          {!readOnly && (
                            <div className="flex flex-col gap-0.5">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5 p-0 text-muted-foreground hover:text-white outline-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
                                onClick={(e) => { e.stopPropagation(); bumpQty(index, 1); }}
                              >
                                <ChevronUp className="h-3 w-3" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5 p-0 text-muted-foreground hover:text-white outline-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
                                onClick={(e) => { e.stopPropagation(); bumpQty(index, -1); }}
                              >
                                <ChevronDown className="h-3 w-3" />
                              </Button>
                            </div>
                          )}
                        </div>
                        </div>
                      )}

                      
                      {!isNewRow && item.product_id && (
                        <div className="w-[17%] text-right">
                          <div className="text-sm font-semibold">
                            {new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(item.total)}
                          </div>
                        </div>
                      )}
                    </div>
                  
                  {!isNewRow && item.product_id && getProductFormId(item.product_id) && (
                    <FormFieldsSection key={`form-${item.product_id}-${index}`} item={item} index={index} />
                  )}
                  </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      {productProfile && (() => {
        const p = (products || []).find((x) => x.id === productProfile?.id);
        const infoItems = [
          p?.codigo_sku ? { value: `SKU: ${p.codigo_sku}` } : null,
          p?.product_category_id ? { value: "Ver categoria", badge: true } : null,
          p?.created_at ? { value: `Produto desde ${new Intl.DateTimeFormat('pt-BR').format(new Date(p.created_at))}` } : null,
        ].filter(Boolean) as any;
        return (
          <RaioXModal
            open={!!productProfile}
            onOpenChange={() => setProductProfile(null)}
            imageUrl={p?.foto_url || null}
            entityTypeLabel="Produto"
            entityName={p?.nome || productProfile.name}
            infoItems={infoItems}
            showPeriodControls
            initialFrom={startOfDay(subDays(new Date(), 6))}
            initialTo={endOfDay(new Date())}
            onPeriodChange={(from, to) => { setRxFrom(from); setRxTo(to); }}
            renderContent={({ periodRange, setLoading }) => (
              productProfile ? <ProductDetailsView id={productProfile.id} embedded periodRange={periodRange} setLoading={setLoading} /> : null
            )}
          />
        );
      })()}
    </div>
  );
});
